import React, { useState } from 'react';
import styled from 'styled-components';
import { API_BASE } from '../apiConfig';

const PageContainer = styled.div`
  width: 100%;
  background: #ffffff;
`;

const HeroSection = styled.section`
  padding: 80px 100px 60px;
  background: linear-gradient(135deg, #453793 0%, #5a4bb5 100%);
  color: white;
  text-align: center;

  h1 {
    font-size: 42px;
    font-weight: 700;
    margin-bottom: 20px;
      color: white;
  }

  p {
    font-size: 20px;
    max-width: 700px;
    margin: 0 auto;
    color: rgba(255, 255, 255, 0.9);
  }

  @media (max-width: 768px) {
    padding: 60px 20px 40px;
    h1 { font-size: 32px; }
    p { font-size: 18px; }
  }
`;

const FormSection = styled.section`
  padding: 80px 100px;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 60px 20px 100px;
  }
`;

const Form = styled.form`
  background: white;
  border-radius: 16px;
  padding: 50px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 15px 50px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 15px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 18px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  box-sizing: border-box;
  font-family: inherit;
  color: #000;
  background: white;

  &:hover {
    border-color: #c0c0c0;
  }

  &:focus {
    border-color: #8ecf41;
    outline: none;
    box-shadow: 0 0 0 3px rgba(142, 207, 65, 0.1);
    transform: translateY(-1px);
  }

  &::placeholder {
    color: #999;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 14px 18px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  box-sizing: border-box;
  font-family: inherit;
  resize: vertical;
  min-height: 140px;
  color: #000;
  background: white;

  &:hover {
    border-color: #c0c0c0;
  }

  &:focus {
    border-color: #8ecf41;
    outline: none;
    box-shadow: 0 0 0 3px rgba(142, 207, 65, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;

const Button = styled.button`
  padding: 15px 40px;
  background-color: #8ecf41;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover:not(:disabled) {
    background-color: #72b237;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(142, 207, 65, 0.3);
  }

  &:disabled {
    background-color: #9ed96a;
    cursor: not-allowed;
    transform: none;
  }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const SuccessCard = styled.div`
  background: linear-gradient(135deg, #8ecf41 0%, #72b237 100%);
  border-radius: 12px;
  padding: 40px 30px;
  text-align: center;
  color: white;
  box-shadow: 0 8px 24px rgba(142, 207, 65, 0.3);
  animation: slideIn 0.4s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  h3 {
    font-size: 24px;
    margin: 0 0 15px 0;
    color: white;
  }

  p {
    font-size: 16px;
    margin: 0;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.95);
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 2px solid #fca5a5;
  border-radius: 8px;
  padding: 16px 20px;
  color: #991b1b;
  font-weight: 500;
  text-align: center;
  margin-top: 20px;
  animation: shake 0.5s ease;

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-10px); }
    75% { transform: translateX(10px); }
  }
`;

function Contact() {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        message: '',
    });

    const [responseMessage, setResponseMessage] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (!formData.name || !formData.email || !formData.message) {
            setResponseMessage({
                type: 'error',
                text: 'Please fill out all required fields.'
            });
            return;
        }

        setIsSubmitting(true);


        try {
            const response = await fetch(`${API_BASE}/api/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setResponseMessage({
                    type: 'success',
                    text: 'Thank you for reaching out! We\'ll be in touch within 24 hours.'
                });
                // Clear form
                setFormData({
                    name: '',
                    phone: '',
                    email: '',
                    message: '',
                });
            } else {
                const result = await response.text();
                setResponseMessage({
                    type: 'error',
                    text: `Error submitting form: ${result}`
                });
            }
        } catch (error) {
            setResponseMessage({
                type: 'error',
                text: `Error submitting form: ${error.message}`
            });
            console.error("Error submitting form:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <PageContainer>
            {/* HERO */}
            <HeroSection>
                <h1>Get in Touch</h1>
                <p>
                    Questions about ZumoDO? Want to know when the next app drops? Partnership ideas, press inquiries, or just want to say hi — we'd love to hear from you.
                </p>
            </HeroSection>

            {/* FORM */}
            <FormSection>
                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label htmlFor="name">Name *</Label>
                        <Input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Your full name"
                            required
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your@email.com"
                            required
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="(123) 456-7890"
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="message">Message *</Label>
                        <TextArea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="What's on your mind?"
                            required
                        />
                    </FormGroup>

                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting && <Spinner />}
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                </Form>

                {responseMessage && responseMessage.type === 'success' && (
                    <SuccessCard>
                        <h3>✓ Message Sent!</h3>
                        <p>Thank you for reaching out! We'll get back to you within 24 hours.</p>
                    </SuccessCard>
                )}

                {responseMessage && responseMessage.type === 'error' && (
                    <ErrorMessage>
                        {responseMessage.text}
                    </ErrorMessage>
                )}
            </FormSection>
        </PageContainer>
    );
}

export default Contact;